import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import getListOrTableField from '@helpers/getListOrTableField';

type ThisAssociatedFields = Pick<QuestionnaireApiModels.LinkedField,
  | 'fieldTemplateId'
  | 'fieldTemplateIndex'
  | 'id'
  | 'linkedFieldTemplateId'
  | 'linkedFieldTemplateIndex'
>;

type ThisFieldTemplate =
  & Pick<QuestionnaireApiModels.FieldTemplate,
    | 'listFieldTemplateId'
    | 'type'
    | 'key'
  >
  & {
    linkedFields: ThisAssociatedFields[];
  };

const getAllLinkedFieldKeys = ({
  field,
  fieldsById,
}: {
  field: ThisFieldTemplate;
  fieldsById: Record<string, ThisFieldTemplate>;
}): string[] => {
  const associatedFields: ThisAssociatedFields[] = [];
  const traversedFields = new Set<string>();
  // First, get all fields that are linked to this field with the following function
  const recursivelyAssociateFields = (linkedFields: ThisAssociatedFields[]): void => {
    linkedFields.forEach(linkedField => {
      if (traversedFields.has(linkedField.id)) {
        return;
      }
      traversedFields.add(linkedField.id);
      associatedFields.push(linkedField);
      const linkedFieldTemplate = fieldsById[linkedField.linkedFieldTemplateId];
      if (linkedFieldTemplate) {
        recursivelyAssociateFields(linkedFieldTemplate.linkedFields
          .filter((f) => f.linkedFieldTemplateId === linkedField.linkedFieldTemplateId
            && f.linkedFieldTemplateIndex === linkedField.linkedFieldTemplateIndex));
      }
    });
  };
  recursivelyAssociateFields(field.linkedFields);
  traversedFields.clear();
  // Next, find all of the keys that need to be updated based on the unique array of linked fields
  const fieldTemplateKeys: string[] = [];
  const populateFieldTemplateKeys = (fieldId: string, index: number | null): void => {
    const linkedFieldTemplate = fieldsById[fieldId];
    if (linkedFieldTemplate?.key) {
      if (index !== null) {
        const listOrTableField = getListOrTableField({
          fieldId,
          fieldsById,
        });
        if (listOrTableField) {
          fieldTemplateKeys.push(`${listOrTableField.key}[${index}].${linkedFieldTemplate.key}`);
        }
      } else {
        fieldTemplateKeys.push(linkedFieldTemplate.key);
      }
    }
  };
  associatedFields.forEach((linkedField) => {
    if (!traversedFields.has(linkedField.fieldTemplateId)) {
      populateFieldTemplateKeys(linkedField.fieldTemplateId, linkedField.fieldTemplateIndex);
      traversedFields.add(linkedField.fieldTemplateId);
    }
    if (!traversedFields.has(linkedField.linkedFieldTemplateId)) {
      populateFieldTemplateKeys(linkedField.linkedFieldTemplateId, linkedField.linkedFieldTemplateIndex);
      traversedFields.add(linkedField.linkedFieldTemplateId);
    }
  });
  return fieldTemplateKeys;
};

export default getAllLinkedFieldKeys;
